import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useYieldCallback } from '@zillow/yield-callback';
import { cx } from '@/styled-system/css';
import NoToursAvailableError from '../NoToursAvailableError';
import VisuallyHidden from 'app/shared/modules/VisuallyHidden';
import dateUtils from 'app/shared/utils/dateUtils';
import circleArrowLeftSmall from 'images/icons/circle-arrow-left-small.svg';
import circleArrowRightSmall from 'images/icons/circle-arrow-right-small.svg';
import { useInstantTourForm, useSelectedTourProvider } from '../../hooks';
import { STEPS, TOUR_TYPES } from '../../constants';
import * as S from './dateTimePicker.styles';
import Text from 'app/shared/core/Text';
import type { RootReduxState } from 'app/shared/flux/reducers';

interface DateTimePickerProps {
  isAvailTimesReqLoading?: boolean;
  setIsPrimaryBtnDisabled?: (disabled: boolean) => void;
}

const computeNewCarouselIdx = (
  localDate: string,
  datesInCarousel: Array<string>,
  numBtnsInCarousel: number,
): number | undefined => {
  if (!localDate || datesInCarousel.length === 0 || !datesInCarousel.includes(localDate)) {
    return 0;
  }

  let carouselIdx;
  for (let idx = 0; idx < datesInCarousel.length; idx++) {
    if (idx % numBtnsInCarousel === 0) {
      carouselIdx = idx;
    }

    if (localDate === datesInCarousel[idx]) {
      break;
    }
  }

  return carouselIdx;
};

// TODO: continue improving accessibility by make kb navigating similar to radio groups.
const DateTimePicker = ({ isAvailTimesReqLoading = false, setIsPrimaryBtnDisabled }: DateTimePickerProps) => {
  const { updateInstantTourForm } = useInstantTourForm();
  const isMobile = useSelector((state: RootReduxState) => state.app.device.isMobile);
  // used to track when isMobile value changes.
  const [isMobileTracker, setIsMobileTracker] = useState(isMobile);
  const numBtnsInCarousel = isMobile ? 3 : 4;
  const selectedDateBtnRef = useRef<HTMLButtonElement>(null);
  const {
    step,
    selectedDate,
    selectedDateTime,
    tourType: selectedTourType,
  } = useSelector((state: RootReduxState) => state.currentListingDetails.instantTourForm);
  const { availableToursMap, zoneId } = useSelector(
    (state: RootReduxState) => state.currentListingDetails.availableToursForListing,
  );
  const datesInCarousel = Object.keys(availableToursMap);
  const noToursAvailable = datesInCarousel.length === 0;
  // Idx used to determine which set of dates to display on the carousel.
  const [carouselIdx, setCarouselIdx] = useState(0);
  const { dateOnly } = useSelectedTourProvider() as { dateOnly?: boolean };

  useEffect(() => {
    // Need to reset carousel start if device screen changes to prevent array out of bounds err.
    if (isMobileTracker !== isMobile) {
      setCarouselIdx(computeNewCarouselIdx(selectedDate, datesInCarousel, numBtnsInCarousel) ?? 0);
      setIsMobileTracker(isMobile);
    }
  }, [datesInCarousel, isMobile, isMobileTracker, numBtnsInCarousel, selectedDate]);

  const getInitialSelectedDateTime = useCallback(
    (dateToUse: string) => {
      if (noToursAvailable) {
        return '';
      }

      if (!selectedDateTime) {
        return availableToursMap[dateToUse][0];
      }

      let newSelectedDateTime = '';
      const selectedHour = selectedDateTime ? new Date(selectedDateTime).getHours() : null;
      const selectedMinute = selectedDateTime ? new Date(selectedDateTime).getMinutes() : null;

      availableToursMap[dateToUse].forEach((availableDateTime) => {
        const availableHour = new Date(availableDateTime).getHours();
        const availableMinute = new Date(availableDateTime).getMinutes();

        if (selectedHour === availableHour && selectedMinute === availableMinute) {
          newSelectedDateTime = availableDateTime;
        }
      });

      if (!newSelectedDateTime) {
        newSelectedDateTime = availableToursMap[dateToUse][0];
      }

      return newSelectedDateTime;
    },
    [availableToursMap, noToursAvailable, selectedDateTime],
  );

  useEffect(() => {
    if (noToursAvailable) {
      setIsPrimaryBtnDisabled?.(true);
      return;
    } else {
      setIsPrimaryBtnDisabled?.(false);
    }

    const initialSelectedDate = datesInCarousel.includes(selectedDate) ? selectedDate : datesInCarousel[0];
    const initialSelectedDateTime = getInitialSelectedDateTime(initialSelectedDate);

    updateInstantTourForm({
      selectedDate: initialSelectedDate,
      selectedDateTime: initialSelectedDateTime,
    });

    setCarouselIdx(computeNewCarouselIdx(initialSelectedDate, datesInCarousel, numBtnsInCarousel) ?? 0);
    // We only want this useEffect to run on initial render:
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedDate && step === STEPS.SELECT_DATE_TIME) {
      selectedDateBtnRef?.current?.focus();
    }
  }, [selectedDate, step]);

  const yieldArrowClick = useYieldCallback((newCarouselIdx: number) => {
    const newSelectedDate = datesInCarousel[newCarouselIdx];
    const newSelectedTime = availableToursMap[newSelectedDate][0];
    setCarouselIdx(newCarouselIdx);

    updateInstantTourForm({
      selectedDate: newSelectedDate,
      selectedDateTime: newSelectedTime,
    });
  });

  const handleArrowClick = useCallback(
    (newCarouselIdx: number) => {
      yieldArrowClick(newCarouselIdx);
    },
    [yieldArrowClick],
  );

  const handleBackArrowClick = useCallback(() => {
    handleArrowClick(carouselIdx - numBtnsInCarousel);
  }, [handleArrowClick, carouselIdx, numBtnsInCarousel]);

  const handleNextArrowClick = useCallback(() => {
    handleArrowClick(carouselIdx + numBtnsInCarousel);
  }, [handleArrowClick, carouselIdx, numBtnsInCarousel]);

  const yieldHandleDateSelect = useYieldCallback((dateBtnValue: string) => {
    updateInstantTourForm({
      selectedDate: dateBtnValue,
      selectedDateTime: availableToursMap[dateBtnValue][0],
    });

    if (dateOnly) {
      setIsPrimaryBtnDisabled?.(false);
    }
  });

  const handleDateSelect = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const dateBtnValue = e.currentTarget.value;
      if (selectedDate === dateBtnValue) {
        return;
      }

      yieldHandleDateSelect(dateBtnValue);
    },
    [yieldHandleDateSelect, selectedDate],
  );

  const yieldHandleTimeSelect = useYieldCallback((dateTime: string) => {
    updateInstantTourForm({ selectedDateTime: dateTime });
    setIsPrimaryBtnDisabled?.(false);
  });

  const handleTimeSelect = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const dateTime = e.currentTarget.value;
      yieldHandleTimeSelect(dateTime);
    },
    [yieldHandleTimeSelect],
  );

  const renderCarouselBtn = useCallback(
    (dateISOString: string, isDisabled: boolean) => {
      const formattedDate = dateUtils.parseISODateString(dateISOString, zoneId, 'yyyy-MM-dd') as string;
      const dayOfWeek = dateUtils.parseISODateString(dateISOString, zoneId, 'EEE') as string;
      const monthAndDay = dateUtils.parseISODateString(dateISOString, zoneId, 'MMM dd') as string;
      const isPressed = selectedDate === formattedDate;

      return (
        <button
          key={formattedDate}
          className={cx(S.dateButton, isPressed && S.dateButtonPressed, isDisabled && S.dateButtonDisabled)}
          disabled={isDisabled}
          aria-pressed={isPressed}
          onClick={handleDateSelect}
          ref={isPressed ? selectedDateBtnRef : null}
          value={formattedDate}
          type="button"
        >
          <span className={S.buttonTextLg}>{dayOfWeek}</span>
          <span className={S.buttonText}>{monthAndDay}</span>
        </button>
      );
    },
    [handleDateSelect, selectedDate, zoneId],
  );

  const renderDisabledCarouselBtn = useCallback(
    (lastAvailableTimeISO: string, daysToAddToLastAvailableDate: number) => {
      const disabledDate = dateUtils.parseISODateString(lastAvailableTimeISO) as Date;
      const originalDayOfMonth = disabledDate.getUTCDate();

      disabledDate.setDate(originalDayOfMonth + daysToAddToLastAvailableDate);
      const isIncrementingMonth = disabledDate.getUTCDate() < originalDayOfMonth;

      if (isIncrementingMonth) {
        disabledDate.setUTCMonth(disabledDate.getUTCMonth() + 1);
        disabledDate.setUTCDate(daysToAddToLastAvailableDate);
      }

      return renderCarouselBtn(disabledDate.toISOString(), true);
    },
    [renderCarouselBtn],
  );

  const renderDateCarousel = useCallback(() => {
    if (noToursAvailable) {
      return null;
    }

    const carouselButtons = [];

    for (let idx = carouselIdx; idx < carouselIdx + numBtnsInCarousel; idx++) {
      if (idx >= datesInCarousel.length) {
        const lastCarouselDate = datesInCarousel[datesInCarousel.length - 1];
        const lastAvailableTimeISO = availableToursMap[lastCarouselDate][0];
        const daysToAddToLastAvailableDate = idx - (datesInCarousel.length - 1);
        carouselButtons.push(renderDisabledCarouselBtn(lastAvailableTimeISO, daysToAddToLastAvailableDate));
      } else {
        const carouselDate = datesInCarousel[idx];
        const availableTimeISO = availableToursMap[carouselDate][0];
        carouselButtons.push(renderCarouselBtn(availableTimeISO, false));
      }
    }

    return (
      <fieldset className={S.dateCarouselContainer}>
        <VisuallyHidden as="legend">Select date</VisuallyHidden>
        <button
          className={cx(S.carouselArrowButton, carouselIdx === 0 && S.carouselArrowButtonDisabled)}
          aria-label="See previous dates"
          disabled={carouselIdx === 0}
          onClick={handleBackArrowClick}
          type="button"
        >
          <img alt="" height="30px" width="30px" src={circleArrowLeftSmall} />
        </button>
        {carouselButtons}
        <button
          className={cx(
            S.carouselArrowButton,
            carouselIdx + numBtnsInCarousel >= datesInCarousel.length && S.carouselArrowButtonDisabled,
          )}
          aria-label="See more dates"
          disabled={carouselIdx + numBtnsInCarousel >= datesInCarousel.length}
          onClick={handleNextArrowClick}
          type="button"
        >
          <img alt="" height="30px" width="30px" src={circleArrowRightSmall} />
        </button>
      </fieldset>
    );
  }, [
    noToursAvailable,
    carouselIdx,
    handleBackArrowClick,
    numBtnsInCarousel,
    datesInCarousel,
    handleNextArrowClick,
    availableToursMap,
    renderDisabledCarouselBtn,
    renderCarouselBtn,
  ]);

  const renderTimeButtons = useCallback(() => {
    if (noToursAvailable || dateOnly || !selectedDate || !availableToursMap[selectedDate]) {
      return null;
    }

    const timeButtons: Array<JSX.Element> = [];

    availableToursMap[selectedDate].forEach((availableDateTimeISOString: string) => {
      const isPressed = selectedDateTime === availableDateTimeISOString;
      const time = dateUtils.parseISODateString(availableDateTimeISOString, zoneId, 'h:mm a') as string;

      const TimeButton = (
        <button
          key={availableDateTimeISOString}
          className={cx(S.timeButton, isPressed && S.timeButtonPressed)}
          aria-pressed={isPressed}
          onClick={handleTimeSelect}
          value={availableDateTimeISOString}
          type="button"
        >
          <span className={S.buttonText}>{time}</span>
        </button>
      );

      timeButtons.push(TimeButton);
    });

    return (
      <fieldset className={S.timeButtonsContainer} style={{ gridTemplateColumns: `repeat(${numBtnsInCarousel}, 1fr)` }}>
        <VisuallyHidden as="legend">Select time</VisuallyHidden>
        {timeButtons}
      </fieldset>
    );
  }, [
    noToursAvailable,
    dateOnly,
    selectedDate,
    availableToursMap,
    numBtnsInCarousel,
    selectedDateTime,
    zoneId,
    handleTimeSelect,
  ]);

  if (isAvailTimesReqLoading) {
    return (
      <div style={{ padding: '10px' }} aria-live="assertive">
        <Text>Loading available tour times...</Text>
      </div>
    );
  }

  return noToursAvailable ? (
    <NoToursAvailableError />
  ) : (
    <>
      <div className={S.header}>Select a tour time</div>
      {dateOnly && selectedTourType === TOUR_TYPES.SELF_GUIDED && (
        <span className={S.subheader}>For self-guided tours, you can visit any time during the day.</span>
      )}
      <form className={S.dateTimeForm}>
        {renderDateCarousel()}
        {renderTimeButtons()}
      </form>
    </>
  );
};

export default DateTimePicker;
